import React, { useEffect } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";

import { UnifiedDataTable } from "../../../layout/unified-data-table/ui/unified-data-table";
import type {
  UnifiedDataTableColumnRenderers,
  UnifiedTableRow,
} from "../../../shared/ui/data-table/model/data-table-contract";
import { ROUTES } from "../../../system/router/routes";
import { ordersProceduresRecord } from "../model/orders-procedures";

import * as Styled from "./orders-procedures.styled";

interface IPatientInfoOutletContext {
  setIsStretchContent: React.Dispatch<React.SetStateAction<boolean>>;
}

export const OrdersProcedures = () => {
  const navigate = useNavigate();
  const { patientID } = useParams();
  const outletContext = useOutletContext<
    IPatientInfoOutletContext | undefined
  >();
  const setIsStretchContent = outletContext?.setIsStretchContent;
  const contentScrollerRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!setIsStretchContent) {
      return;
    }

    setIsStretchContent(true);

    return () => {
      setIsStretchContent(false);
    };
  }, [setIsStretchContent]);

  const columnRenderers = React.useMemo<UnifiedDataTableColumnRenderers>(
    () => ({
      orderDetails: () => (
        <Styled.RowIcon className="row-action-icon" aria-hidden="true">
          <i className="pi pi-arrow-right" aria-hidden="true" />
        </Styled.RowIcon>
      ),
      orderName: ({ value }) => (
        <Styled.OrderNameCell>{String(value ?? "—")}</Styled.OrderNameCell>
      ),
    }),
    [],
  );

  const handleRowClick = React.useCallback(
    (row: UnifiedTableRow) => {
      if (!patientID || typeof row.id !== "string") {
        return;
      }

      navigate(
        ROUTES.PATIENT_DETAILS_ORDERS_PROCEDURES_DETAILS.replace(
          ":patientID",
          patientID,
        ).replace(":orderID", row.id),
      );
    },
    [navigate, patientID],
  );

  return (
    <Styled.Container>
      <Styled.Title>Orders & Procedures</Styled.Title>
      <UnifiedDataTable
        record={ordersProceduresRecord}
        columnRenderers={columnRenderers}
        onRowClick={handleRowClick}
        useSharedSectionNav
        scrollContainerRef={contentScrollerRef}
      />
    </Styled.Container>
  );
};
